import { authFetch } from './auth-fetch';

/**
 * Projects, repositories and GitHub (`12 §7` and the GitHub half of `12 §3`).
 *
 * Third file after `endpoints.ts` and `organization.ts`, and split for the same
 * reason: one place per area of the API where you can see, at a glance,
 * everything this frontend asks the server for. Same rule as the other two — no
 * client-side modelling and no renaming of fields.
 */

export interface Project {
  id: string;
  company_id: string;
  name: string;
  slug: string;
  description: string | null;
  goals: string | null;
  status: 'planning' | 'active' | 'paused' | 'archived';
  tech_stack: string[];
  lead_employee_id: string | null;
  autonomy_policy_id: string | null;
  settings: Record<string, unknown>;
  archived_at: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * Whether one of the two operational integrations is usable from a project.
 *
 * Three words and not a boolean because the third one is somebody else's
 * problem: `no_connection` means the company has no Jenkins or no kubeconfig,
 * and binding the project cannot fix that.
 */
export type IntegrationState = 'configured' | 'not_configured' | 'no_connection';

/** What each row of the listing carries next to the project itself. */
export interface ProjectSummary {
  tasks: {
    total: number;
    /** Still on the board: neither draft, done, cancelled nor failed. */
    open: number;
    done: number;
    /** Creation of the newest non-draft task. */
    last_requested_at: string | null;
    /** Completion of the newest task that reached `done`. */
    last_finished_at: string | null;
  };
  /** Active repositories. */
  repositories: number;
  members: number;
  lead_name: string | null;
  kubernetes: {
    state: IntegrationState;
    context: string | null;
    namespaces: string[];
    autonomy: 'read_only' | 'approval' | 'autonomous' | null;
  };
  jenkins: {
    state: IntegrationState;
    job_name: string | null;
    autonomy: 'read_only' | 'approval' | 'autonomous' | null;
  };
}

/** A row of `GET /companies/{id}/projects`: the project plus its `summary`. */
export interface ProjectListItem extends Project {
  summary: ProjectSummary;
}

export interface ProjectDetail extends Project {
  repositories: Repository[];
  members: ProjectMember[];
  /** Present and null until phases 9 and 6 fill them in; see `ProjectView::detail`. */
  budget: unknown | null;
  autonomy_policy: unknown | null;
}

/**
 * The eight roles of `08 §1.2`, in one place.
 *
 * A constant and not a literal repeated per screen: the router of `09 §3.3`
 * filters by this before it scores anybody, so a role is a routing input, and a
 * list that had drifted between two screens would offer somewhere a role that
 * decides nothing.
 */
export const PROJECT_ROLES = [
  'lead',
  'architect',
  'developer',
  'reviewer',
  'qa',
  'designer',
  'devops',
  'observer',
] as const;

export type ProjectRole = (typeof PROJECT_ROLES)[number];

/** `employee_project_access.access_level` (`03 §4`). */
export type AccessLevel = 'none' | 'read' | 'write' | 'admin';

export interface ProjectMember {
  id: string;
  project_id: string;
  employee_id: string;
  employee_name: string;
  employee_slug: string;
  job_title: string;
  /** Sin versión publicada no se le puede abrir sesión: asignarle una tarea la bloquea al arrancar. */
  has_published_profile: boolean;
  role_in_project: ProjectRole;
  allocation_pct: number | null;
  /** `none` when no access row exists, which is what having none means. */
  access_level: AccessLevel;
  /** Null is every repository of the project, which is the ordinary case. */
  repository_ids: string[] | null;
  created_at: string;
}

/** What one row of a staffing plan does when the plan is applied. */
export type StaffingOp = 'keep' | 'add' | 'update' | 'remove' | 'hire';

/**
 * One row of `staffing-plan.v1.json`.
 *
 * `ref` is the whole identity: the employee's slug for anybody who already works
 * here, and a name the plan chose for a hire.
 *
 * **A removal is a row and never an absence.** The server refuses a plan that
 * leaves out somebody who is on the project today, so a truncated answer from
 * the model is a finding rather than a project quietly emptied — which is why
 * the screen's bin icon marks a row `remove` instead of deleting it.
 */
export interface StaffingPlanMember {
  ref: string;
  op: StaffingOp;
  role_in_project: ProjectRole;
  allocation_pct?: number | null;
  access_level: AccessLevel;
  /** Repository **names** — not ids, which is what `ProjectMember` carries. Null is every repository of the project. */
  repositories?: string[] | null;
  reason?: string | null;
  /** Only for `op: hire`. */
  name?: string | null;
  template_key?: string | null;
  job_title?: string | null;
  seniority?: string | null;
  department_slug?: string | null;
  team_slug?: string | null;
  reports_to_slug?: string | null;
}

export interface StaffingPlan {
  members: StaffingPlanMember[];
}

export const EMPTY_STAFFING_PLAN: StaffingPlan = { members: [] };

/**
 * `repositories.settings`, as `config/schemas/repository-settings.v1.json`
 * defines it.
 *
 * A `null` command is meaningful and is not the same as a missing key: it says
 * «we looked and there is none» where an absent key would say «nobody has
 * looked» (`08 §4`). The editor has to preserve that distinction, so the type
 * carries it.
 */
export interface RepositorySettings {
  commands: {
    install: string | null;
    build: string | null;
    test: string | null;
    lint: string | null;
    typecheck: string | null;
  };
  branch_prefix: string;
  commit_convention: 'conventional' | 'plain';
  merge_strategy: 'squash' | 'merge' | 'rebase';
  delete_branch_on_merge: boolean;
  protected_paths: string[];
  require_ci: boolean;
  ci_check_names: string[];
  max_files_changed_auto_merge: number;
  max_lines_changed_auto_merge: number;
}

export interface Repository {
  id: string;
  project_id: string;
  provider: string;
  external_id: string | null;
  owner_login: string;
  name: string;
  full_name: string;
  visibility: 'private' | 'internal' | 'public';
  default_branch: string;
  clone_url: string | null;
  html_url: string | null;
  role: string | null;
  /** `creating` is the reservation of `08 §3.2` step 5, not a fourth kind of repository. */
  status: 'creating' | 'active' | 'archived' | 'disconnected';
  status_reason: string | null;
  created_by_task_id: string | null;
  settings: RepositorySettings;
  last_synced_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface RepositoryConvention {
  id: string;
  repository_id: string;
  content: string;
  source: 'generated' | 'manual';
  generated_from_sha: string | null;
  version: number;
  created_at: string;
  updated_at: string;
}

/**
 * What `POST /repositories/preview-name` answers.
 *
 * **Advisory on purpose.** The reservation is what decides a name, and between
 * this answer and the create call another agent can take it (case G-16), so a
 * screen must present it as a preview and never as a promise.
 */
export interface NamePreview {
  name: string;
  full_name: string;
  available: boolean;
  /** Reason codes, empty when the name is usable. */
  violations: string[];
}

/** One repository the company's GitHub credential can see. */
export interface GithubRepository {
  external_id: string;
  full_name: string;
  name: string;
  private: boolean;
  default_branch: string;
  html_url: string;
  connectable: boolean;
  reason: string | null;
}

/**
 * `mode` and `trimmed` are the whole GitHub settings screen in two fields:
 * `github_app` means tokens are cut down per execution, `github_pat` means they
 * are not and the manager has to be told so permanently (`08 §2.2`).
 */
export interface GithubAccessState {
  data: GithubRepository[];
  mode: 'github_app' | 'github_pat';
  trimmed: boolean;
}

export const projects = {
  list: (companyId: string, includeArchived = false) =>
    authFetch<{ data: ProjectListItem[] }>(
      `/companies/${companyId}/projects${includeArchived ? '?include_archived=1' : ''}`,
    ),
  create: (companyId: string, body: Record<string, unknown>) =>
    authFetch<Project>(`/companies/${companyId}/projects`, { method: 'POST', body }),
  get: (id: string) => authFetch<ProjectDetail>(`/projects/${id}`),
  update: (id: string, body: Record<string, unknown>) =>
    authFetch<Project>(`/projects/${id}`, { method: 'PATCH', body }),
  /**
   * The settings document, which is a separate body from the one above for the
   * reason the company's is: it is governed by a JSON Schema and not by a DTO.
   * The patch is merged deeply and the **result** validated, so a misspelled key
   * comes back as a 422 naming it rather than being silently ignored.
   */
  updateSettings: (id: string, patch: Record<string, unknown>) =>
    authFetch<Project>(`/projects/${id}/settings`, { method: 'PATCH', body: patch }),
  archive: (id: string) => authFetch<Project>(`/projects/${id}/archive`, { method: 'POST', body: {} }),
  members: (id: string) => authFetch<{ data: ProjectMember[] }>(`/projects/${id}/members`),
  addMember: (id: string, body: { employee_id: string; role_in_project: string; allocation_pct?: number | null }) =>
    authFetch<ProjectMember>(`/projects/${id}/members`, { method: 'POST', body }),
  removeMember: (id: string, employeeId: string) =>
    authFetch<void>(`/projects/${id}/members/${employeeId}`, { method: 'DELETE' }),
};

export const repositories = {
  list: (projectId: string, includeInactive = true) =>
    authFetch<{ data: Repository[] }>(
      `/projects/${projectId}/repositories${includeInactive ? '?include_inactive=1' : ''}`,
    ),
  previewName: (projectId: string, repoComponent: string) =>
    authFetch<NamePreview>(`/projects/${projectId}/repositories/preview-name`, {
      method: 'POST',
      body: { repo_component: repoComponent },
    }),
  connect: (projectId: string, body: { full_name: string; role?: string | null; apply_branch_protection?: boolean }) =>
    authFetch<Repository>(`/projects/${projectId}/repositories/connect`, { method: 'POST', body }),
  create: (projectId: string, body: { repo_component: string; role?: string | null; description?: string | null }) =>
    authFetch<Repository>(`/projects/${projectId}/repositories`, { method: 'POST', body }),
  get: (id: string) => authFetch<Repository>(`/repositories/${id}`),
  updateSettings: (id: string, patch: Record<string, unknown>) =>
    authFetch<Repository>(`/repositories/${id}/settings`, { method: 'PATCH', body: patch }),
  detectCommands: (id: string) =>
    authFetch<Repository>(`/repositories/${id}/detect-commands`, { method: 'POST', body: {} }),
  sync: (id: string) =>
    authFetch<{ repository: Repository; changes: string[] }>(`/repositories/${id}/sync`, {
      method: 'POST',
      body: {},
    }),
  disconnect: (id: string) => authFetch<void>(`/repositories/${id}`, { method: 'DELETE' }),
  conventions: (id: string) => authFetch<RepositoryConvention>(`/repositories/${id}/conventions`),
  putConventions: (id: string, content: string) =>
    authFetch<RepositoryConvention>(`/repositories/${id}/conventions`, { method: 'PUT', body: { content } }),
  conventionVersions: (id: string) =>
    authFetch<{ data: RepositoryConvention[] }>(`/repositories/${id}/conventions/versions`),
  regenerateConventions: (id: string) =>
    authFetch<void>(`/repositories/${id}/conventions/regenerate`, { method: 'POST', body: {} }),
};

export const github = {
  installUrl: (companyId: string) =>
    authFetch<{ url: string; state: string; expires_in: number }>(`/companies/${companyId}/github/install-url`),
  /** The callback of `08 §2.1`, relayed by the frontend. A POST: it binds an external account to a company. */
  install: (companyId: string, body: { installation_id: number; state: string }) =>
    authFetch<Record<string, unknown>>(`/companies/${companyId}/github/installations`, { method: 'POST', body }),
  /** Calling it **rotates**: a new secret replaces the one every repository was registered with. */
  webhookSecret: (companyId: string) =>
    authFetch<{ url: string; secret: string; content_type: string }>(
      `/companies/${companyId}/github/webhook-secret`,
      { method: 'POST', body: {} },
    ),
  /**
   * The secret in force, read back rather than minted (`08 §2.2`).
   *
   * A request of its own and not a field of the company: the value is readable,
   * but never as a side effect of loading a screen, and the server audits each
   * read. `secret` is null when none has been generated yet.
   */
  revealWebhookSecret: (companyId: string) =>
    authFetch<{ url: string; secret: string | null; content_type: string }>(
      `/companies/${companyId}/github/webhook-secret`,
    ),
  repositories: (companyId: string) => authFetch<GithubAccessState>(`/companies/${companyId}/github/repositories`),
};
